// pages/product/product-unit-stock/import.jsx
import { useMemo, useState } from "react"
import { Modal } from "components"
import axios from "axios"
import nProgress from "nprogress"
import toast from "react-hot-toast"
import { lang } from "config"

import SelectDescription from "components/forms/select-description"
import useBranchRepositories from "repositories/branch"

const Content = ({ onSuccess }) => {
  const [file, setFile] = useState(null)
  const [outletId, setOutletId] = useState("")
  const [errorMessages, setErrorMessages] = useState([])
  const [submitting, setSubmitting] = useState(false)

  const { data: outletSelection, isLoading: isLoadingOutletSelection } =
    useBranchRepositories({ paginate: false })

  const outletOptions = useMemo(() => outletSelection || [], [outletSelection])

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!outletId) {
      toast.error("Silakan pilih outlet terlebih dahulu")
      return
    }

    if (!file) {
      toast.error("Silakan pilih file Excel terlebih dahulu")
      return
    }

    const url = `${process.env.REACT_APP_INVENTORY_API_URL}/product-unit-stocks/import`

    const formData = new FormData()
    formData.append("outlet_id", outletId)
    formData.append("file", file)

    nProgress.start()
    setSubmitting(true)

    try {
      const response = await axios.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })

      toast.success(response?.data?.message || "Import stock berhasil diproses")

      setErrorMessages([])
      if (onSuccess) onSuccess()
    } catch (error) {
      const status = error?.response?.status

      if (status === 422) {
        const data = error.response.data
        const errs = Array.isArray(data?.errors)
          ? data.errors
          : typeof data?.errors === "object"
          ? Object.values(data.errors).flat()
          : [data?.message || "Validasi gagal."]

        setErrorMessages(errs.filter(Boolean))
        toast.error(data?.message || "Validasi gagal")
      } else {
        toast.error(
          error?.response?.data?.message || "Gagal meng-import stock"
        )
      }
    } finally {
      nProgress.done()
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="mt-6 space-y-6 text-xs">
      <div className="space-y-2">
        <p className="text-sm text-neutral-700">
          Upload file Excel dengan format:
        </p>
        <ul className="pl-5 space-y-1 list-disc text-[11px] text-neutral-600">
          <li>
            Header baris pertama minimal:
            <code className="px-1 mx-1 rounded bg-neutral-100">id</code>,
            <code className="px-1 mx-1 rounded bg-neutral-100">stock</code>,
            <code className="px-1 mx-1 rounded bg-neutral-100">HPP</code>
          </li>
          <li>
            Kolom <code className="px-1 mx-1 rounded bg-neutral-100">name</code>{" "}
            boleh ada (opsional) tapi tidak dipakai sebagai key.
          </li>
          <li>
            1 baris = 1 product unit. Stock harus angka {`(>= 0)`}.
          </li>
        </ul>
      </div>

      <div className="space-y-2">
        <label className="block text-xs font-medium text-neutral-700">
          Outlet
        </label>
        <SelectDescription
          onChange={(value) => {
            setOutletId(value)
            setErrorMessages([])
          }}
          selection={outletOptions}
          isLoading={isLoadingOutletSelection}
          value={outletId}
          keyValue={(value) => value.id}
          title={(value) => value.name}
          description={(value) => value.code || value.id}
          placeholder="Select Outlet"
        />
        {outletId && (
          <p className="text-[11px] text-neutral-500">
            Selected outlet:{" "}
            <span className="font-medium">
              {outletOptions.find((o) => o.id === outletId)?.name || outletId}
            </span>
          </p>
        )}
      </div>

      <div className="space-y-2">
        <label className="block text-xs font-medium text-neutral-700">
          File Excel
        </label>
        <input
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={(e) => {
            const f = e.target.files?.[0]
            setFile(f || null)
            setErrorMessages([])
          }}
          className="block w-full text-xs text-neutral-700 border border-neutral-200 rounded-xl cursor-pointer file:mr-3 file:px-3 file:py-2 file:text-xs file:font-medium file:border-0 file:rounded-xl file:bg-neutral-800 file:text-white hover:file:bg-neutral-900"
        />
        {file && (
          <p className="text-[11px] text-neutral-500">
            Selected file: <span className="font-medium">{file.name}</span>
          </p>
        )}
      </div>

      {errorMessages.length > 0 && (
        <div className="p-3 text-[11px] border rounded-xl border-red-200 bg-red-50 text-red-700 max-h-40 overflow-auto">
          <div className="font-semibold mb-1">{lang?.error || "Error"}:</div>
          <ul className="pl-4 space-y-1 list-disc">
            {errorMessages.map((msg, idx) => (
              <li key={idx}>{msg}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex items-center space-x-2">
        <button
          type="submit"
          disabled={submitting}
          className="items-center px-6 py-3 text-white transition bg-neutral-800 rounded-xl active:hover:scale-90 disabled:opacity-60"
        >
          <span>{submitting ? "Meng-upload..." : "Import Excel Stock"}</span>
        </button>
      </div>
    </form>
  )
}

const Import = ({ onSuccess }) => {
  const [isOpen, setIsOpen] = useState(false)

  const openModal = () => setIsOpen(true)
  const closeModal = () => setIsOpen(false)

  const handleSuccess = () => {
    closeModal()
    if (onSuccess) onSuccess()
  }

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className="inline-flex items-center px-4 py-3 space-x-2 text-xs transition border border-neutral-200 bg-neutral-50 rounded-xl active:hover:scale-90"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-4 h-4"
          width={24}
          height={24}
          viewBox="0 0 24 24"
          strokeWidth={1}
          stroke="currentColor"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
          <path d="M4 4h16v4h-16z" />
          <path d="M4 12h16v8h-16z" />
          <path d="M10 8v8l3 -3l3 3v-8" />
        </svg>
        <span>Import Excel</span>
      </button>

      <Modal
        isOpen={isOpen}
        onClose={closeModal}
        title="Import Product Unit Stock"
        content={<Content onSuccess={handleSuccess} />}
      />
    </>
  )
}

export default Import
